import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Heart, Clock, Flame, Cake, Salad, Coffee, LayoutGrid, Utensils, Sparkles, Trash2, Loader2, ExternalLink, Link2, Clipboard, Droplet } from "lucide-react";

const CATEGORIES = [
  { v: "", l: "الكل", icon: LayoutGrid },
  { v: "sweet", l: "حلو", icon: Cake },
  { v: "savory", l: "مالح", icon: Salad },
  { v: "beverage", l: "مشروبات", icon: Coffee },
  { v: "sauces", l: "صلصات", icon: Droplet },
];
const CAT_LABEL = { sweet: "حلو", savory: "مالح", beverage: "مشروبات", sauces: "صلصات" };
const PLATFORM_LABEL = { tiktok: "TikTok", instagram: "Instagram", youtube: "YouTube", facebook: "Facebook", twitter: "X" };

export default function RecipesPage() {
  // All filter state lives in the URL so that browser Back / on-page back link
  // restores the EXACT view the user was on (tab + category + subcategory +
  // search + favorites). Combined with the scroll-restore effect below, this
  // gives a "return to where I left off" experience.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") === "mine" ? "mine" : "public";
  const catFromUrl = searchParams.get("cat") || "";
  const subFromUrl = searchParams.get("sub") || "";
  const qFromUrl = searchParams.get("q") || "";
  const favsFromUrl = searchParams.get("favs") === "1";

  const [tab, setTabState] = useState(tabFromUrl);
  const [category, setCategoryState] = useState(catFromUrl);
  const [subcategory, setSubcategoryState] = useState(subFromUrl);
  const [q, setQState] = useState(qFromUrl);
  const [showFavs, setShowFavsState] = useState(favsFromUrl);

  // Re-sync local state whenever the URL changes (Back button, deep-link).
  useEffect(() => {
    setTabState(tabFromUrl);
    setCategoryState(catFromUrl);
    setSubcategoryState(subFromUrl);
    setQState(qFromUrl);
    setShowFavsState(favsFromUrl);
  }, [tabFromUrl, catFromUrl, subFromUrl, qFromUrl, favsFromUrl]);

  // Write-through helpers — update state AND URL together.
  const patchUrl = (next) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === "" || v == null || v === false) params.delete(k);
      else params.set(k, v === true ? "1" : v);
    });
    setSearchParams(params, { replace: true });
  };
  const setTab = (next) => { setTabState(next); patchUrl({ tab: next === "mine" ? "mine" : "" }); };
  const setCategory = (next) => { setCategoryState(next); setSubcategoryState(""); patchUrl({ cat: next || "", sub: "" }); };
  const setSubcategory = (next) => { setSubcategoryState(next); patchUrl({ sub: next || "" }); };
  const setQ = (next) => { setQState(next); patchUrl({ q: next || "" }); };
  const setShowFavs = (next) => { setShowFavsState(next); patchUrl({ favs: next ? "1" : "" }); };

  const [recipes, setRecipes] = useState([]);
  const [myImports, setMyImports] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  useEffect(() => {
    api.get("/recipes/subcategories").then((r) => setSubcategories(r.data)).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  // ---- Scroll restoration ----
  // Save current scroll before navigating to a recipe detail; restore it when
  // the user comes back. Keyed by the full URL (path+search) so that different
  // filters (cat=savory vs cat=sweet) each remember their own scroll position.
  const scrollKey = `recipes-scroll::${window.location.pathname}${window.location.search}`;
  const rememberScroll = () => {
    try { sessionStorage.setItem(scrollKey, String(window.scrollY || 0)); } catch {}
  };
  // Also persist on unmount (covers browser Back, in-app router navigations,
  // and Link clicks that don't have onClick handlers wired up).
  const scrollKeyRef = useRef(scrollKey);
  useEffect(() => { scrollKeyRef.current = scrollKey; }, [scrollKey]);
  useEffect(() => () => {
    try { sessionStorage.setItem(scrollKeyRef.current, String(window.scrollY || 0)); } catch {}
  }, []);
  // IDs of imports whose Stream upload just completed — shows a "الفيديو جاهز" pulse for ~15s
  const [justReadyIds, setJustReadyIds] = useState(new Set());
  // Active background import jobs — shown as ghost/shimmer cards at the top of "my imports"
  const [activeJobs, setActiveJobs] = useState([]);  // [{ job_id, url, status, message, started_at }]

  // Pagination — infinite scroll loads 24 at a time
  const PAGE_SIZE = 24;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const loadPublic = async ({ append = false } = {}) => {
    if (append) setLoadingMore(true); else setLoading(true);
    const params = { slim: true, limit: PAGE_SIZE, skip: append ? recipes.length : 0 };
    if (q) params.q = q;
    if (category) params.category = category;
    if (subcategory) params.subcategory = subcategory;
    const endpoint = showFavs ? "/recipes/favorites" : "/recipes";
    const { data } = await api.get(endpoint, { params });
    // Favorites endpoint is not paginated — treat it as one full page.
    if (showFavs) {
      setRecipes(data);
      setHasMore(false);
    } else {
      setRecipes(append ? [...recipes, ...data] : data);
      setHasMore(data.length === PAGE_SIZE);
    }
    if (append) setLoadingMore(false); else setLoading(false);
  };

  const loadMine = async ({ append = false } = {}) => {
    if (append) setLoadingMore(true); else setLoading(true);
    const params = { limit: PAGE_SIZE, skip: append ? myImports.length : 0 };
    if (category) params.category = category;
    if (subcategory) params.subcategory = subcategory;
    const { data } = await api.get("/user-recipes", { params });
    setMyImports(append ? [...myImports, ...data] : data);
    setHasMore(data.length === PAGE_SIZE);
    if (append) setLoadingMore(false); else setLoading(false);
  };

  useEffect(() => {
    // Reset accumulator whenever any filter changes.
    setRecipes([]); setMyImports([]); setHasMore(false);
    if (tab === "public") loadPublic(); else loadMine();
    // eslint-disable-next-line
  }, [tab, category, subcategory, showFavs, q]);

  // After the current view finishes its FIRST load, restore any scroll position
  // saved for this exact URL. We don't restore on subsequent loads (filter
  // changes) because that would fight the user's intent to see fresh results.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (loading || didRestoreRef.current) return;
    try {
      const y = parseInt(sessionStorage.getItem(scrollKey) || "0", 10);
      if (y > 0) window.scrollTo({ top: y, behavior: "instant" });
    } catch {}
    didRestoreRef.current = true;
  }, [loading, scrollKey]);

  // Infinite scroll — fetch next page when sentinel enters viewport.
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (tab === "public") loadPublic({ append: true });
        else loadMine({ append: true });
      }
    }, { rootMargin: "400px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
    // eslint-disable-next-line
  }, [hasMore, loading, loadingMore, tab, recipes.length, myImports.length]);

  // Auto-poll when any imported recipe is still uploading to Cloudflare Stream.
  // As soon as one flips to "ready", we mark it as `justReady` for a brief celebratory pulse.
  useEffect(() => {
    if (tab !== "mine") return;
    const hasPending = myImports.some((r) => r.stream_status === "uploading");
    if (!hasPending) return;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/user-recipes", { params: category ? { category } : {} });
        // Detect flips: was `uploading` before, is `ready` now
        const prevPending = new Set(myImports.filter((r) => r.stream_status === "uploading").map((r) => r.id));
        const flipped = data.filter((r) => prevPending.has(r.id) && r.stream_status === "ready").map((r) => r.id);
        if (flipped.length > 0) {
          setJustReadyIds((prev) => {
            const next = new Set(prev);
            flipped.forEach((id) => next.add(id));
            return next;
          });
          // Clear the "just ready" flag after 15s (badge naturally disappears)
          flipped.forEach((id) => {
            setTimeout(() => {
              setJustReadyIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              });
            }, 15000);
          });
        }
        setMyImports(data);
      } catch (_) { /* ignore transient poll failures */ }
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [tab, myImports, category]);

  // Poll each active import job every 2.5s. On completion → refresh myImports and drop the ghost.
  useEffect(() => {
    if (activeJobs.length === 0) return;
    const timer = setInterval(async () => {
      let anyCompleted = false;
      const nextJobs = await Promise.all(activeJobs.map(async (j) => {
        if (j.status === "completed" || j.status === "failed") return j;
        try {
          const { data } = await api.get(`/user-recipes/import/jobs/${j.job_id}`);
          if (data.status === "completed") {
            anyCompleted = true;
            toast.success(`تم استيراد: ${data.recipe_title || "الوصفة"}`);
            return { ...j, status: "completed", message: data.message };
          }
          if (data.status === "failed") {
            // No toast — the card itself shows the error prominently inside
            // the video area (less disruptive, less scary for the user).
            return { ...j, status: "failed", message: data.message, error: data.error };
          }
          return { ...j, status: data.status, message: data.message };
        } catch (_) {
          return j;
        }
      }));
      setActiveJobs(nextJobs);
      if (anyCompleted) {
        // Refresh the list so real cards show up
        try {
          const { data } = await api.get("/user-recipes", { params: category ? { category } : {} });
          setMyImports(data);
        } catch (_) {}
        // Drop completed/failed jobs after a small delay so the "🎉 تم!" moment is visible
        setTimeout(() => {
          setActiveJobs((prev) => prev.filter((x) => x.status !== "completed" && x.status !== "failed"));
        }, 1500);
      }
    }, 2500);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [activeJobs.length, category]);

  const startImport = (jobId, url) => {
    setActiveJobs((prev) => [...prev, { job_id: jobId, url, status: "queued", message: "في الانتظار...", started_at: Date.now() }]);
    setTab("mine");   // auto-switch so user sees the progress card
    setImportOpen(false);  // close the dialog immediately
    toast.info("بدأ الاستيراد — راقب التقدم في تبويب وصفاتي");
  };

  const toggleFav = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    const { data } = await api.post(`/recipes/${id}/favorite`);
    setRecipes((rs) => rs.map((r) => r.id === id ? { ...r, is_favorite: data.is_favorite } : r));
  };

  const deleteImport = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("حذف الوصفة المستوردة؟")) return;
    try {
      await api.delete(`/user-recipes/${id}`);
      toast.success("تم الحذف");
      loadMine();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto lg:mx-0 lg:max-w-none">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold" data-testid="recipes-page-title">الوصفات الصحية</h1>
        <p className="text-muted-foreground mt-1.5 text-sm">اكتشف وصفات، احفظ المفضل، أو استورد وصفاتك من TikTok وInstagram.</p>
      </div>

      {/* Compact import CTA — inline row, no huge padding */}
      <button
        onClick={() => setImportOpen(true)}
        className="w-full text-start relative overflow-hidden rounded-xl bg-gradient-to-l from-purple-600 via-pink-500 to-orange-500 p-3.5 shadow-lg shadow-purple-500/15 hover:shadow-xl hover:brightness-110 transition-all group"
        data-testid="import-cta-banner"
      >
        <div className="relative flex items-center gap-3 text-white">
          <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur grid place-items-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-display font-bold text-sm sm:text-base">استيراد وصفة من TikTok أو Instagram</span>
              <span className="text-[9px] bg-white/25 rounded-full px-1.5 py-0.5 font-medium hidden sm:inline">جديد</span>
            </div>
            <p className="text-[11px] sm:text-xs text-white/85 mt-0.5 line-clamp-1">الصق الرابط — الذكاء الاصطناعي يستخرج المكونات والسعرات فوراً</p>
          </div>
          <div className="shrink-0 inline-flex items-center gap-1 bg-white text-purple-700 rounded-full px-3 py-1.5 text-xs font-semibold group-hover:bg-white/90">
            <Link2 className="h-3.5 w-3.5" /> ابدأ
          </div>
        </div>
      </button>

      {/* Tabs: Public vs My imports (count in the label, no separate chip) */}
      <div className="flex gap-2 border-b border-border" data-testid="recipes-tabs">
        <button
          onClick={() => setTab("public")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "public" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="tab-public"
        >
          وصفات المنصة
        </button>
        <button
          onClick={() => setTab("mine")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "mine" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="tab-mine"
        >
          وصفاتي المستوردة {myImports.length > 0 ? `(${myImports.length})` : ""}
        </button>
      </div>

      {/* Search + filter chips together — search on top, chips scroll horizontally on mobile so they never wrap */}
      {tab === "public" && (
        <form onSubmit={(e) => { e.preventDefault(); loadPublic(); }} className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن وصفة..." data-testid="recipes-search" className="flex-1" />
          <Button type="submit" variant="outline" data-testid="recipes-search-submit">بحث</Button>
        </form>
      )}

      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 no-scrollbar snap-x" data-testid="category-chips">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = category === c.v;
          return (
            <button
              key={c.v || "all"}
              onClick={() => { setCategory(c.v); setSubcategory(""); }}
              className={`shrink-0 snap-start inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-all text-sm ${
                active ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`chip-cat-${c.v || "all"}`}
            >
              <Icon className="h-3.5 w-3.5" /> {c.l}
            </button>
          );
        })}
        {tab === "public" && (
          <button
            onClick={() => setShowFavs(!showFavs)}
            className={`shrink-0 snap-start inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-all text-sm ms-auto ${
              showFavs ? "border-accent bg-accent text-accent-foreground shadow-sm" : "border-border bg-card hover:border-accent/40"
            }`}
            data-testid="btn-toggle-favs"
          >
            <Heart className={`h-3.5 w-3.5 ${showFavs ? "fill-current" : ""}`} /> مفضلاتي
          </button>
        )}
      </div>

      {/* Level-2 sub-category chips — only shown when a main category is picked */}
      {category && (subcategories[category] || []).length > 0 && (
        <div
          className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-1 no-scrollbar snap-x"
          data-testid="subcategory-chips"
        >
          <button
            onClick={() => setSubcategory("")}
            className={`shrink-0 snap-start px-3 py-1 rounded-full border text-xs transition-all ${
              !subcategory ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:border-primary/40"
            }`}
            data-testid="chip-sub-all"
          >
            الكل
          </button>
          {(subcategories[category] || []).map((s) => (
            <button
              key={s}
              onClick={() => setSubcategory(subcategory === s ? "" : s)}
              className={`shrink-0 snap-start px-3 py-1 rounded-full border text-xs transition-all ${
                subcategory === s ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`chip-sub-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center text-muted-foreground py-12">جاري التحميل...</div>
      ) : tab === "public" ? (
        recipes.length === 0 ? (
          <div className="p-12 border border-dashed rounded-xl text-center text-muted-foreground" data-testid="recipes-empty">
            {showFavs ? "لا وصفات في المفضلة بعد" : "لا توجد وصفات مطابقة"}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recipes.map((r) => (
              <Link to={`/recipes/${r.id}`} key={r.id} onClick={rememberScroll} data-testid={`recipe-card-${r.id}`}>
                <Card className="overflow-hidden hover:-translate-y-1 transition-transform h-full">
                  <div className="relative">
                    {r.image_url ? (
                      <img src={r.image_url} alt={r.title} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
                    ) : (
                      <div className="w-full aspect-[4/3] bg-secondary grid place-items-center text-muted-foreground">
                        <Utensils className="h-10 w-10 opacity-40" />
                      </div>
                    )}
                    <button onClick={(e) => toggleFav(r.id, e)}
                            className="absolute top-3 end-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur grid place-items-center hover:scale-110 transition-transform"
                            data-testid={`btn-fav-${r.id}`}>
                      <Heart className={`h-4 w-4 ${r.is_favorite ? "text-accent fill-current" : "text-foreground"}`} />
                    </button>
                    {r.category && CAT_LABEL[r.category] && (
                      <span className="absolute top-3 start-3 bg-white/90 backdrop-blur text-xs px-2 py-1 rounded-full font-medium">
                        {CAT_LABEL[r.category]}
                      </span>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    <h3 className="font-display font-semibold line-clamp-1">{r.title}</h3>
                    {r.subcategory && (
                      <span
                        className="inline-block text-[10px] bg-secondary text-secondary-foreground rounded-full px-2 py-0.5"
                        data-testid={`recipe-sub-${r.id}`}
                      >
                        {r.subcategory}
                      </span>
                    )}
                    <p className="text-sm text-muted-foreground line-clamp-2">{r.short_description}</p>
                    {r.fitness_note && (
                      <p
                        className="text-[11px] leading-relaxed text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/70 dark:border-emerald-800/60 rounded-md p-2 line-clamp-3"
                        data-testid={`recipe-benefit-${r.id}`}
                      >
                        <span className="me-1">{r.health_style === "strict" ? "💪" : "🥗"}</span>
                        {r.fitness_note}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {(r.prep_minutes || 0) + (r.cook_minutes || 0)}د</span>
                      <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-primary" /> {r.calories_per_serving || 0} سعرة</span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )
      ) : (
        // My imports tab
        (myImports.length === 0 && activeJobs.length === 0) ? (
          <div className="p-12 border border-dashed rounded-xl text-center text-muted-foreground space-y-2" data-testid="imports-empty">
            <div>لم تستورد أي وصفة بعد.</div>
            <Button onClick={() => setImportOpen(true)} variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4" /> استورد أول وصفة
            </Button>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {activeJobs.map((job) => (
              <ImportingCard key={job.job_id} job={job} />
            ))}
            {myImports.map((r) => (
              <ImportCard key={r.id} r={r} onDelete={deleteImport} justReady={justReadyIds.has(r.id)} />
            ))}
          </div>
        )
      )}

      {/* Infinite-scroll sentinel + loader — keeps the app fast at 1000+ recipes */}
      {hasMore && !loading && (
        <div ref={sentinelRef} className="py-8 flex justify-center" data-testid="load-more-sentinel">
          {loadingMore ? (
            <div className="text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> تحميل المزيد...
            </div>
          ) : (
            <div className="h-1 w-1" />
          )}
        </div>
      )}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onJobStarted={startImport} />
    </div>
  );
}

// ---------------------------------------------------------------------------

// Ghost card shown while a background import job is running.
// Non-clickable (pointer-events on inner content), blurred, shimmer + progress bar + rotating tip.
const STAGE_META = {
  queued:      { pct: 5,  label: "في الانتظار...",                    icon: "⏳" },
  downloading: { pct: 25, label: "جاري تنزيل الفيديو",                 icon: "⬇️" },
  extracting:  { pct: 60, label: "الذكاء الاصطناعي يحلّل الوصفة",       icon: "🧠" },
  uploading:   { pct: 85, label: "جاري رفع الفيديو للتخزين الدائم",     icon: "☁️" },
  completed:   { pct: 100, label: "🎉 تم! جاري تحضير البطاقة...",        icon: "✅" },
  failed:      { pct: 100, label: "فشلت العملية — حاول مجدداً",          icon: "❌" },
};

const IMPORT_TIPS = [
  "💡 هل تعلم؟ يمكنك استيراد من TikTok وInstagram",
  "🎯 الذكاء الاصطناعي يترجم الوصفات الإنجليزية للعربية تلقائياً",
  "🔒 وصفاتك المستوردة خاصة بك فقط — لا يراها أحد غيرك",
  "🎬 الفيديو يُحفظ في وصفتك لتشاهده مستقبلاً بدون إنترنت خارجي",
  "📝 يمكنك تعديل المكونات والخطوات بعد الاستيراد",
];

function ImportingCard({ job }) {
  const [tipIdx, setTipIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTipIdx((i) => (i + 1) % IMPORT_TIPS.length), 4000);
    const e = setInterval(() => setElapsed(Math.floor((Date.now() - job.started_at) / 1000)), 1000);
    return () => { clearInterval(t); clearInterval(e); };
  }, [job.started_at]);

  const meta = STAGE_META[job.status] || STAGE_META.queued;
  const isFailed = job.status === "failed";
  const isDone = job.status === "completed";

  return (
    <div
      className={`relative rounded-xl overflow-hidden border-2 h-full transition-all ${
        isFailed ? "border-amber-300 bg-amber-50" : isDone ? "border-emerald-400 bg-emerald-50 shadow-lg animate-video-ready" :
        "border-purple-300 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50"
      }`}
      data-testid={`importing-card-${job.job_id}`}
      style={{ pointerEvents: "none" }}
    >
      {/* Shimmer overlay while in progress */}
      {!isDone && !isFailed && (
        <div className="absolute inset-0 shimmer-overlay" aria-hidden="true" />
      )}

      {/* Image area — on failure, show the maintenance message in-place
          (where the video would appear), instead of a disruptive toast popup. */}
      <div className={`relative aspect-[4/3] grid place-items-center overflow-hidden ${isFailed ? "bg-amber-50" : "bg-foreground/5"}`}>
        {isFailed ? (
          <div className="text-center px-6 py-4 space-y-2">
            <div className="text-4xl mb-1">🛠️</div>
            <div className="font-display font-bold text-sm text-amber-900 leading-relaxed">
              هذه الميزة تحت الصيانة لتحسين جودة الخدمة
            </div>
            <div className="text-xs text-amber-700/80 leading-relaxed">
              ستكون متوفّرة خلال ساعات — نشكر صبرك 💛
            </div>
          </div>
        ) : (
          <div className="text-6xl opacity-90 animate-float-slow">{meta.icon}</div>
        )}
        <div className="absolute top-3 start-3 bg-white/80 backdrop-blur text-[10px] px-2 py-0.5 rounded-full font-semibold">
          {isFailed ? "صيانة" : "قيد المعالجة"}
        </div>
        {!isFailed && !isDone && (
          <div className="absolute bottom-3 end-3 bg-white/80 backdrop-blur text-[10px] px-2 py-0.5 rounded-full text-foreground/70">
            {elapsed}ث
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          {!isFailed && !isDone && <Loader2 className="h-4 w-4 animate-spin text-purple-600 shrink-0" />}
          <h3 className={`font-display font-semibold text-sm line-clamp-1 ${isFailed ? "text-amber-900" : isDone ? "text-emerald-800" : "text-purple-900"}`}>
            {isFailed ? "ميزة الاستيراد تحت الصيانة" : meta.label}
          </h3>
        </div>

        {/* Progress bar — hidden when failed (maintenance state has no progress) */}
        {!isFailed && (
          <div className="space-y-1">
            <div className="h-1.5 rounded-full bg-foreground/5 overflow-hidden">
              <div
                className={`h-full transition-all duration-700 ${
                  isDone ? "bg-emerald-500" : "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"
                }`}
                style={{ width: `${meta.pct}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[10px] text-foreground/50">
              <span>{meta.pct}%</span>
              {!isDone && <span>⏱ ~{Math.max(0, 40 - elapsed)}ث تقريباً</span>}
            </div>
          </div>
        )}

        {/* Rotating tip (hidden when failed — the image area already carries the message) */}
        {!isFailed && (
          <div className="text-xs leading-relaxed rounded-md p-2 min-h-[2.5rem] bg-white/60 text-foreground/70">
            {isDone ? "✨ تم حفظ الوصفة! جاري تحديث القائمة..." : IMPORT_TIPS[tipIdx]}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ImportCard({ r, onDelete, justReady }) {
  const uploading = r.stream_status === "uploading";
  return (
    <Link to={`/imported/${r.id}`} data-testid={`import-card-${r.id}`}>
      <Card className={`overflow-hidden hover:-translate-y-1 transition-transform h-full relative ${justReady ? "ring-2 ring-emerald-400 animate-video-ready" : ""}`}>
        <div className="relative bg-black">
          {r.image_url ? (
            <img src={r.image_url} alt={r.title} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
          ) : r.source_thumbnail ? (
            <img src={r.source_thumbnail} alt="" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
          ) : (
            <div className="w-full aspect-[4/3] grid place-items-center text-white/40">
              <Utensils className="h-10 w-10" />
            </div>
          )}
          <span className="absolute top-3 start-3 bg-white/90 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
            <Link2 className="h-3 w-3" /> {PLATFORM_LABEL[r.source_platform] || "رابط"}
          </span>
          {r.category && (
            <span className="absolute top-3 end-3 bg-primary/90 text-primary-foreground text-xs px-2 py-1 rounded-full font-medium">
              {CAT_LABEL[r.category] || r.category}
            </span>
          )}
          {/* Stream upload status badge */}
          {(uploading || justReady) && (
            <div className="absolute inset-x-3 bottom-3" data-testid={`stream-badge-${r.id}`}>
              {justReady ? (
                <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg animate-badge-pulse">
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping" />
                  الفيديو جاهز
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur text-white/95 text-[11px] px-2 py-1 rounded-full">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  الفيديو قيد الرفع...
                </span>
              )}
            </div>
          )}
          <button onClick={(e) => onDelete(r.id, e)} className="absolute bottom-3 end-3 h-8 w-8 rounded-full bg-white/90 grid place-items-center text-destructive hover:scale-110 transition-transform" data-testid={`btn-del-import-${r.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <h3 className="font-display font-semibold line-clamp-1">{r.title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{r.short_description}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {(r.prep_minutes || 0) + (r.cook_minutes || 0)}د</span>
            <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-primary" /> {r.calories_per_serving || 0} سعرة</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function ImportDialog({ open, onClose, onJobStarted }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!url.trim()) { toast.error("الصق رابط الفيديو"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/user-recipes/import", { url: url.trim() });
      if (!data.job_id) throw new Error("no job_id");
      // Hand off to parent — parent closes dialog + shows ghost card in the mine tab.
      onJobStarted(data.job_id, url.trim());
      setUrl("");
    } catch (e) {
      toast.error(extractError(e, "فشل الاستيراد. تأكد من الرابط وحاول مجدداً."));
    } finally {
      setLoading(false);
    }
  };

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) { setUrl(text.trim()); toast.success("تم اللصق"); }
      else toast.error("الحافظة فارغة");
    } catch { toast.error("تعذّر الوصول للحافظة — الصق يدوياً"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !loading && (setUrl(""), onClose())}>
      <DialogContent dir="rtl" className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            استيراد وصفة من فيديو
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Step-by-step visual guide */}
          <div className="space-y-3">
            <div className="text-sm font-semibold text-foreground">اتّبع الخطوات:</div>

            <StepRow n={1} title="افتح TikTok أو Instagram" desc="من داخل التطبيق، افتح الفيديو الذي يعرض الوصفة." icon="📱" />
            <StepRow n={2} title="اضغط زر المشاركة (Share)" desc={<>على TikTok: <b>سهم مشاركة</b> على يمين الفيديو ↗️<br/>على Instagram: أيقونة <b>الطائرة الورقية</b> ✈️ تحت الفيديو</>} icon="📤" />
            <StepRow n={3} title='اختر "Copy Link" (نسخ الرابط)' desc="عادة في الصف الثاني من قائمة المشاركة." icon="📋" />
            <StepRow n={4} title="ارجع هنا والصق الرابط بالأسفل" desc="ثم اضغط الزر البنفسجي للاستخراج." icon="📥" active />
          </div>

          {/* Paste input — highlighted */}
          <div className="space-y-2 border-2 border-purple-300 bg-purple-50/50 rounded-xl p-3">
            <label className="text-sm font-bold text-purple-900 flex items-center gap-1">
              <Link2 className="h-4 w-4" /> الصق الرابط هنا ⬇️
            </label>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://www.tiktok.com/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                dir="ltr"
                disabled={loading}
                data-testid="import-url-input"
                className="bg-background text-start"
              />
              <Button type="button" variant="outline" onClick={paste} disabled={loading} data-testid="btn-paste-clipboard" className="shrink-0 gap-1">
                <Clipboard className="h-3.5 w-3.5" /> لصق
              </Button>
            </div>
            <p className="text-[11px] text-purple-800/70">يعمل مع: TikTok · Instagram Reels</p>
          </div>

          {/* What happens next */}
          <div className="rounded-lg bg-secondary/50 border p-3 text-xs space-y-1.5">
            <div className="font-semibold text-foreground">بعد ما تضغط "استخراج":</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
              <span>⏳ ينزّل الفيديو</span>
              <span>👁️ يشاهده الذكاء الاصطناعي</span>
              <span>📝 يستخرج المكونات والخطوات</span>
              <span>🔢 يقدّر السعرات والوقت</span>
              <span>🌐 يترجم إن كان بالإنجليزية</span>
              <span>🔒 يحفظها في وصفاتك الخاصة</span>
            </div>
            <div className="pt-1 text-[11px] text-muted-foreground">قد يستغرق 1-3 دقائق حسب طول الفيديو — لا تغلق الصفحة.</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>إلغاء</Button>
          <Button onClick={submit} disabled={loading || !url.trim()} className="bg-purple-600 hover:bg-purple-700 gap-2" data-testid="btn-do-import">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> جاري الإرسال...</> : <><Sparkles className="h-4 w-4" /> استخراج الوصفة</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepRow({ n, title, desc, icon, active }) {
  return (
    <div className={`flex items-start gap-3 rounded-lg p-3 border-2 transition-colors ${active ? "border-purple-300 bg-purple-50" : "border-transparent bg-secondary/40"}`}>
      <div className={`h-9 w-9 rounded-full grid place-items-center shrink-0 font-bold text-sm ${active ? "bg-purple-600 text-white" : "bg-secondary text-foreground"}`}>
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm flex items-center gap-2">
          <span>{title}</span>
          <span className="text-lg">{icon}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
    </div>
  );
}
